import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  BarChart as ReBarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import { authenticatedApiCall } from '../../utils/apiClient';
import AnalyticsSettingsPanel, { useAnalyticsSettings, SettingsButton } from '../common/AnalyticsSettings';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const AVAILABLE_METRICS = [
  { id: 'totalRecords', label: 'Total Records', icon: '📋', defaultVisible: true },
  { id: 'totalQty', label: 'Total Quantity', icon: '📦', defaultVisible: true },
  { id: 'totalCAD', label: 'Total CAD', icon: '💵', defaultVisible: true },
  { id: 'totalHR', label: 'Total Hours', icon: '⏱️', defaultVisible: true },
];

const AVAILABLE_CHARTS = [
  { id: 'byType', label: 'By Type', icon: '📊', defaultVisible: true, defaultType: 'bar' },
  { id: 'byMaterial', label: 'By Material', icon: '🔧', defaultVisible: true, defaultType: 'bar' },
  { id: 'byPipingClass', label: 'By Piping Class', icon: '📏', defaultVisible: true, defaultType: 'bar' },
];

// Curated grouping dimensions for Summaries + Pivot. Keys match the post-Plan-1
// `mto` columns. Only those present in the data are shown, so columns that aren't
// always populated (see EXTRA_DIMENSION_CANDIDATES) appear automatically.
const DIMENSION_FIELDS = [
  { key: 'material', label: 'Material' },
  { key: 'matbore', label: 'MatBore' },
  { key: 'fluid_code', label: 'Fluid Code' },
  { key: 'piping_class', label: 'Piping Class' },
  { key: 'fabrication_shop', label: 'Fab Shop' },
  { key: 'type', label: 'Type' },
  { key: 'sch', label: 'Sch' },
  { key: 'bore', label: 'Bore' },
  { key: 'rating', label: 'Rating' },
  { key: 'end_type', label: 'End Type' },
  { key: 'seam_type', label: 'Seam Type' },
  { key: 'join_type', label: 'Joint Type' },
  { key: 'priority', label: 'Priority' },
  { key: 'iso_sheet_number', label: 'ISO Sheet #' },
  { key: 'iso_version', label: 'ISO Version' },
  { key: 'spool_number', label: 'Spool #' },
  { key: 'rev', label: 'Rev' },
];
// Dimensions that may not exist on every MTO row but should be picked up if present.
const EXTRA_DIMENSION_CANDIDATES = [
  { key: 'system', label: 'System' },
  { key: 'subsystem', label: 'Subsystem' },
  { key: 'sov', label: 'SOV' },
  { key: 'wbs', label: 'WBS' },
  { key: 'length', label: 'Length' },
];
// Numeric measures available for summaries + pivot (post-Plan-1 columns).
const MEASURE_FIELDS = [
  { key: 'qty', label: 'Quantity' },
  { key: 'total_hours', label: 'Labour Hours' },
  { key: 'mh_per_ea', label: 'MH per EA' },
  { key: 'unit_price_cad', label: 'Unit Price CAD' },
  { key: 'total_material_cad', label: 'Material Cost CAD' },
  { key: 'total_labour_cad', label: 'Labour Cost CAD' },
  { key: 'grand_total_cad', label: 'Grand Total CAD' },
  { key: 'grand_total_usd', label: 'Grand Total USD' },
  { key: 'grand_total_cad_after_factors', label: 'Grand Total CAD (after factors)' },
  { key: 'joints', label: 'Joints' },
  { key: 'total_joints', label: 'Total Joints' },
  { key: 'dia_in', label: 'Dia In' },
];
// Measure keys that should be formatted as currency.
const MONEY_KEYS = new Set([
  'unit_price_cad', 'total_material_cad', 'total_labour_cad',
  'grand_total_cad', 'grand_total_usd', 'grand_total_cad_after_factors',
]);
const AGGREGATIONS = [
  { key: 'sum', label: 'Sum' },
  { key: 'avg', label: 'Average' },
  { key: 'count', label: 'Count' },
  { key: 'min', label: 'Min' },
  { key: 'max', label: 'Max' },
];

const keyPresent = (data, key) => data.some((d) => d[key] !== undefined && d[key] !== null && String(d[key]).length > 0);

const toNum = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/** Total joints for a row: prefer total_joints, else joints × qty. */
const rowTotalJoints = (d) => {
  const tj = parseFloat(d.total_joints);
  if (Number.isFinite(tj)) return tj;
  const j = parseFloat(d.joints);
  const q = parseFloat(d.qty);
  if (Number.isFinite(j) && Number.isFinite(q)) return j * q;
  return Number.isFinite(j) ? j : 0;
};

/** Total dia-in for a row: prefer total_dia_in, else dia_in × qty. */
const rowTotalDiaIn = (d) => {
  const t = parseFloat(d.total_dia_in);
  if (Number.isFinite(t)) return t;
  const dia = parseFloat(d.dia_in);
  const q = parseFloat(d.qty);
  if (Number.isFinite(dia) && Number.isFinite(q)) return dia * q;
  return 0;
};

/** Pipe family for ft totals: Pipe, T-Pipe, V-Pipe (any spacing/case). */
const isPipeType = (d) => {
  const t = String(d.type || '').trim().toLowerCase().replace(/[\s_]+/g, '-');
  return t === 'pipe' || t === 't-pipe' || t === 'v-pipe';
};

/** Split a total using the estimate shop ratio (from Factors settings). */
const splitByShopRatio = (total, shopRatio) => {
  const t = toNum(total);
  const s = Math.min(1, Math.max(0, Number.isFinite(shopRatio) ? shopRatio : 0.6));
  return { total: t, shop: t * s, field: t * (1 - s) };
};

const dimVal = (d, key) => (
  (d[key] === undefined || d[key] === null || String(d[key]).length === 0) ? 'Unspecified' : String(d[key])
);

const emptyAgg = () => ({
  count: 0, qty: 0, total_hours: 0, grand_total_cad: 0,
  total_material_cad: 0, total_labour_cad: 0, joints: 0,
});

const addAgg = (bucket, d) => {
  bucket.count += 1;
  bucket.qty += parseFloat(d.qty) || 0;
  bucket.total_hours += parseFloat(d.total_hours) || 0;
  bucket.grand_total_cad += parseFloat(d.grand_total_cad) || 0;
  bucket.total_material_cad += parseFloat(d.total_material_cad) || 0;
  bucket.total_labour_cad += parseFloat(d.total_labour_cad) || 0;
  bucket.joints += rowTotalJoints(d);
};

const aggregateValues = (rows, measureKey, agg) => {
  if (agg === 'count') return rows.length;
  const nums = rows.map((r) => {
    if (measureKey === 'total_joints' || measureKey === 'joints') return rowTotalJoints(r);
    return parseFloat(r[measureKey]);
  }).filter((n) => !isNaN(n));
  if (nums.length === 0) return 0;
  if (agg === 'sum') return nums.reduce((a, b) => a + b, 0);
  if (agg === 'avg') return nums.reduce((a, b) => a + b, 0) / nums.length;
  if (agg === 'min') return Math.min(...nums);
  if (agg === 'max') return Math.max(...nums);
  return 0;
};

/**
 * MtoAnalyticsDashboard - Analytics Dashboard for MTO Log
 * Displays MTO quantities, material categories, and estimation analysis
 */
const MtoAnalyticsDashboard = ({ data, onClose, estimateNumber, jobNumber }) => {
  const [selectedView, setSelectedView] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);
  const { instance, accounts } = useMsal();

  // Change History (Plan 9): lazy-loaded from mto_change_event.
  const [changeEvents, setChangeEvents] = useState(null);
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeError, setChangeError] = useState('');

  // Shop/field ratio from estimate Factors settings (user-configured).
  const [shopRatio, setShopRatio] = useState(0.6);
  const [shopPctInput, setShopPctInput] = useState('60');
  const [shopRatioSaving, setShopRatioSaving] = useState(false);

  useEffect(() => {
    if (!estimateNumber) { setShopRatio(0.6); setShopPctInput('60'); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const resp = await authenticatedApiCall(instance, accounts, 'GET', `/mto/factors?estimate_number=${encodeURIComponent(estimateNumber)}`);
        const s = resp?.data?.settings || {};
        const ratio = s.shop_ratio == null ? 0.6 : Number(s.shop_ratio);
        if (!cancelled && Number.isFinite(ratio)) {
          setShopRatio(ratio);
          setShopPctInput(String(Math.round(ratio * 1000) / 10));
        }
      } catch (_) { /* keep default */ }
    })();
    return () => { cancelled = true; };
  }, [instance, accounts, estimateNumber]);

  const applyShopRatioLocal = useCallback(() => {
    const pct = parseFloat(shopPctInput);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) return;
    setShopRatio(pct / 100);
  }, [shopPctInput]);

  const saveShopRatio = useCallback(async () => {
    const pct = parseFloat(shopPctInput);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) return;
    const ratio = pct / 100;
    setShopRatio(ratio);
    if (!estimateNumber) return;
    setShopRatioSaving(true);
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', '/mto/settings', {
        estimate_number: estimateNumber,
        shop_ratio: ratio,
      });
    } catch (_) { /* display still updates locally */ }
    finally { setShopRatioSaving(false); }
  }, [shopPctInput, estimateNumber, instance, accounts]);

  const loadChangeEvents = useCallback(async () => {
    if (!estimateNumber && !jobNumber) { setChangeEvents([]); return; }
    setChangeLoading(true);
    setChangeError('');
    try {
      const qs = [];
      if (estimateNumber) qs.push(`estimate_number=${encodeURIComponent(estimateNumber)}`);
      if (jobNumber) qs.push(`job_number=${encodeURIComponent(jobNumber)}`);
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/mto/change-events?${qs.join('&')}`);
      setChangeEvents((resp && resp.events) || []);
    } catch (err) {
      setChangeError(err?.message || 'Failed to load change history');
      setChangeEvents([]);
    } finally {
      setChangeLoading(false);
    }
  }, [instance, accounts, estimateNumber, jobNumber]);

  useEffect(() => {
    if (selectedView === 'changes' && changeEvents === null && !changeLoading) {
      loadChangeEvents();
    }
  }, [selectedView, changeEvents, changeLoading, loadChangeEvents]);

  // Summaries tab state (two grouping layers)
  const [summaryDim, setSummaryDim] = useState('material');
  const [summaryDim2, setSummaryDim2] = useState('join_type'); // second grouping layer
  const [summaryMeasure, setSummaryMeasure] = useState('grand_total_cad');

  // Pivot tab state
  const [pivotRow, setPivotRow] = useState('material');
  const [pivotCol, setPivotCol] = useState('');
  const [pivotMeasure, setPivotMeasure] = useState('grand_total_cad');
  const [pivotAgg, setPivotAgg] = useState('sum');

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState } = usePresence('mtoAnalytics', 'global', true);
  
  const {
    settings,
    updateSetting,
    toggleMetric,
    toggleChart,
    setChartType,
    resetToDefaults,
    isMetricVisible,
    isChartVisible,
  } = useAnalyticsSettings('mto_log', AVAILABLE_METRICS, AVAILABLE_CHARTS);

  const analytics = useMemo(() => {
    const emptyShopField = { total: 0, shop: 0, field: 0 };
    if (!data || data.length === 0) {
      return {
        totalRecords: 0,
        totalQty: 0,
        totalCAD: 0,
        totalHR: 0,
        totalJoints: 0,
        totalMaterialCAD: 0,
        totalLabourCAD: 0,
        uniqueTypes: 0,
        uniqueMaterials: 0,
        uniqueLines: 0,
        hoursSF: emptyShopField,
        pipeFt: 0,
        diaInSF: emptyShopField,
        jointsSF: emptyShopField,
        pipeBySize: [],
        diaInByJoinType: [],
        byType: [],
        byMaterial: [],
        byPipingClass: [],
        byFabShop: [],
        byPriority: [],
        byIso: [],
        byJoinType: [],
      };
    }

    const totalRecords = data.length;
    // qty arrives as numeric/string from Postgres — coerce safely ("" / null → 0)
    const totalQty = data.reduce((sum, d) => {
      const q = Number(d.qty);
      return sum + (Number.isFinite(q) ? q : 0);
    }, 0);
    const totalCAD = data.reduce((sum, d) => sum + (parseFloat(d.grand_total_cad) || 0), 0);
    const totalHR = data.reduce((sum, d) => sum + (parseFloat(d.total_hours) || 0), 0);
    const totalJoints = data.reduce((sum, d) => sum + rowTotalJoints(d), 0);
    const totalMaterialCAD = data.reduce((sum, d) => sum + (parseFloat(d.total_material_cad) || 0), 0);
    const totalLabourCAD = data.reduce((sum, d) => sum + (parseFloat(d.total_labour_cad) || 0), 0);

    // Totals + Shop/Field using the estimate shop ratio (from Factors / user input).
    let pipeFt = 0;
    let diaInTotal = 0;
    data.forEach((d) => {
      if (isPipeType(d)) pipeFt += toNum(d.qty);
      diaInTotal += rowTotalDiaIn(d);
    });
    const hoursSF = splitByShopRatio(totalHR, shopRatio);
    const diaInSF = splitByShopRatio(diaInTotal, shopRatio);
    const jointsSF = splitByShopRatio(totalJoints, shopRatio);

    // Pipe × Size pivot (qty / ft) — includes Pipe, T-Pipe, V-Pipe
    const pipeSizeMap = {};
    data.forEach((d) => {
      if (!isPipeType(d)) return;
      const size = dimVal(d, 'size');
      if (!pipeSizeMap[size]) pipeSizeMap[size] = { size, qty: 0 };
      pipeSizeMap[size].qty += toNum(d.qty);
    });
    const pipeBySize = Object.values(pipeSizeMap).sort((a, b) => {
      const na = parseFloat(a.size);
      const nb = parseFloat(b.size);
      if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
      return String(a.size).localeCompare(String(b.size), undefined, { numeric: true });
    });

    // Joint Type × Total Dia In summary
    const diaJoinMap = {};
    data.forEach((d) => {
      const jt = dimVal(d, 'join_type');
      if (!diaJoinMap[jt]) diaJoinMap[jt] = { name: jt, diaIn: 0 };
      diaJoinMap[jt].diaIn += rowTotalDiaIn(d);
    });
    const diaInByJoinType = Object.values(diaJoinMap).sort((a, b) => b.diaIn - a.diaIn);

    // Hours by ISO sheet (top 10) — the "where is the work" view.
    const isoMap = {};
    data.forEach(d => {
      const iso = d.iso_sheet_number || 'Unspecified';
      if (!isoMap[iso]) isoMap[iso] = { name: iso, hr: 0, totalCAD: 0, count: 0 };
      isoMap[iso].hr += parseFloat(d.total_hours) || 0;
      isoMap[iso].totalCAD += parseFloat(d.grand_total_cad) || 0;
      isoMap[iso].count++;
    });
    const byIso = Object.values(isoMap).sort((a, b) => b.hr - a.hr).slice(0, 10);

    const uniqueTypes = new Set(data.map(d => d.type).filter(Boolean)).size;
    const uniqueMaterials = new Set(data.map(d => d.material).filter(Boolean)).size;
    const uniqueLines = new Set(data.map(d => d.line_number).filter(Boolean)).size;

    // By Type
    const typeMap = {};
    data.forEach(d => {
      const type = d.type || 'Unspecified';
      if (!typeMap[type]) {
        typeMap[type] = { name: type, count: 0, qty: 0, totalCAD: 0, hr: 0 };
      }
      typeMap[type].count++;
      typeMap[type].qty += parseFloat(d.qty) || 0;
      typeMap[type].totalCAD += parseFloat(d.grand_total_cad) || 0;
      typeMap[type].hr += parseFloat(d.total_hours) || 0;
    });
    const byType = Object.values(typeMap).sort((a, b) => b.totalCAD - a.totalCAD);

    // By Material
    const matMap = {};
    data.forEach(d => {
      const mat = d.material || 'Unspecified';
      if (!matMap[mat]) {
        matMap[mat] = { name: mat, count: 0, qty: 0, totalCAD: 0 };
      }
      matMap[mat].count++;
      matMap[mat].qty += parseFloat(d.qty) || 0;
      matMap[mat].totalCAD += parseFloat(d.grand_total_cad) || 0;
    });
    const byMaterial = Object.values(matMap).sort((a, b) => b.totalCAD - a.totalCAD).slice(0, 15);

    // By Piping Class
    const classMap = {};
    data.forEach(d => {
      const cls = d.piping_class || 'Unspecified';
      if (!classMap[cls]) {
        classMap[cls] = { name: cls, count: 0, qty: 0, totalCAD: 0 };
      }
      classMap[cls].count++;
      classMap[cls].qty += parseFloat(d.qty) || 0;
      classMap[cls].totalCAD += parseFloat(d.grand_total_cad) || 0;
    });
    const byPipingClass = Object.values(classMap).sort((a, b) => b.count - a.count).slice(0, 15);

    // By Fab Shop
    const shopMap = {};
    data.forEach(d => {
      const shop = d.fabrication_shop || 'Unspecified';
      if (!shopMap[shop]) {
        shopMap[shop] = { name: shop, count: 0, qty: 0, totalCAD: 0, hr: 0 };
      }
      shopMap[shop].count++;
      shopMap[shop].qty += parseFloat(d.qty) || 0;
      shopMap[shop].totalCAD += parseFloat(d.grand_total_cad) || 0;
      shopMap[shop].hr += parseFloat(d.total_hours) || 0;
    });
    const byFabShop = Object.values(shopMap).sort((a, b) => b.count - a.count).slice(0, 10);

    // By Priority
    const prioMap = {};
    data.forEach(d => {
      const prio = d.priority || 'Unassigned';
      if (!prioMap[prio]) {
        prioMap[prio] = { name: prio, count: 0, totalCAD: 0 };
      }
      prioMap[prio].count++;
      prioMap[prio].totalCAD += parseFloat(d.grand_total_cad) || 0;
    });
    const byPriority = Object.values(prioMap).sort((a, b) => b.count - a.count);

    // Joint Type → sum of total joints (for overview card/table)
    const jtMap = {};
    data.forEach((d) => {
      const jt = dimVal(d, 'join_type');
      if (!jtMap[jt]) jtMap[jt] = { name: jt, count: 0, joints: 0, qty: 0, totalCAD: 0 };
      jtMap[jt].count += 1;
      jtMap[jt].joints += rowTotalJoints(d);
      jtMap[jt].qty += parseFloat(d.qty) || 0;
      jtMap[jt].totalCAD += parseFloat(d.grand_total_cad) || 0;
    });
    const byJoinType = Object.values(jtMap).sort((a, b) => b.joints - a.joints);

    return {
      totalRecords,
      totalQty,
      totalCAD,
      totalHR,
      totalJoints,
      totalMaterialCAD,
      totalLabourCAD,
      uniqueTypes,
      uniqueMaterials,
      uniqueLines,
      avgUnitPrice: totalQty > 0 ? totalCAD / totalQty : 0,
      hoursSF,
      pipeFt,
      diaInSF,
      jointsSF,
      pipeBySize,
      diaInByJoinType,
      byType,
      byMaterial,
      byPipingClass,
      byFabShop,
      byPriority,
      byIso,
      byJoinType,
    };
  }, [data, shopRatio]);

  // Dimensions/measures actually present in the current data set.
  // Always keep Joint Type available for the 2-layer summary / joint pivot.
  const dimensions = useMemo(() => {
    if (!data || data.length === 0) return DIMENSION_FIELDS;
    const present = (list) => list.filter((f) => f.key === 'join_type' || keyPresent(data, f.key));
    return [...present(DIMENSION_FIELDS), ...present(EXTRA_DIMENSION_CANDIDATES)];
  }, [data]);

  const measures = useMemo(() => {
    if (!data || data.length === 0) return MEASURE_FIELDS;
    return MEASURE_FIELDS.filter((f) => keyPresent(data, f.key));
  }, [data]);

  // Summaries: 1 or 2 grouping layers with standard measures.
  const summaryRows = useMemo(() => {
    if (!data || data.length === 0) return [];
    const has2 = !!summaryDim2 && summaryDim2 !== summaryDim;
    const map = {};
    data.forEach((d) => {
      const g1 = dimVal(d, summaryDim);
      const g2 = has2 ? dimVal(d, summaryDim2) : null;
      const key = has2 ? `${g1}\u0001${g2}` : g1;
      if (!map[key]) {
        map[key] = { ...emptyAgg(), name: has2 ? `${g1} · ${g2}` : g1, group1: g1, group2: g2 };
      }
      addAgg(map[key], d);
    });
    return Object.values(map).sort((a, b) => {
      const c = String(a.group1).localeCompare(String(b.group1));
      if (c !== 0) return c;
      if (has2) return (b[summaryMeasure] || b.count) - (a[summaryMeasure] || a.count);
      return (b[summaryMeasure] || b.count) - (a[summaryMeasure] || a.count);
    });
  }, [data, summaryDim, summaryDim2, summaryMeasure]);

  // Primary-only rollup for the summary chart when two layers are active.
  const summaryChartRows = useMemo(() => {
    if (!summaryDim2 || summaryDim2 === summaryDim) return summaryRows;
    const map = {};
    summaryRows.forEach((r) => {
      if (!map[r.group1]) map[r.group1] = { ...emptyAgg(), name: r.group1 };
      map[r.group1].count += r.count;
      map[r.group1].qty += r.qty;
      map[r.group1].total_hours += r.total_hours;
      map[r.group1].grand_total_cad += r.grand_total_cad;
      map[r.group1].total_material_cad += r.total_material_cad;
      map[r.group1].total_labour_cad += r.total_labour_cad;
      map[r.group1].joints += r.joints;
    });
    return Object.values(map).sort((a, b) => (b[summaryMeasure] || b.count) - (a[summaryMeasure] || a.count));
  }, [summaryRows, summaryDim, summaryDim2, summaryMeasure]);

  // Pivot: rows x optional columns, aggregating a measure.
  const pivot = useMemo(() => {
    if (!data || data.length === 0) return { rowKeys: [], colKeys: [], cells: {}, rowTotals: {}, colTotals: {}, grand: 0 };
    const valOf = (d, key) => ((d[key] === undefined || d[key] === null || String(d[key]).length === 0) ? 'Unspecified' : String(d[key]));
    const groups = {}; // rowKey -> colKey -> rows[]
    const rowSet = new Set();
    const colSet = new Set();
    data.forEach((d) => {
      const rk = valOf(d, pivotRow);
      const ck = pivotCol ? valOf(d, pivotCol) : '__total__';
      rowSet.add(rk);
      colSet.add(ck);
      groups[rk] = groups[rk] || {};
      groups[rk][ck] = groups[rk][ck] || [];
      groups[rk][ck].push(d);
    });
    const rowKeys = Array.from(rowSet).sort();
    const colKeys = Array.from(colSet).sort();
    const cells = {};
    const rowTotals = {};
    const colTotals = {};
    let grand = 0;
    rowKeys.forEach((rk) => {
      cells[rk] = {};
      const allRowRows = [];
      colKeys.forEach((ck) => {
        const rows = (groups[rk] && groups[rk][ck]) || [];
        cells[rk][ck] = aggregateValues(rows, pivotMeasure, pivotAgg);
        allRowRows.push(...rows);
        colTotals[ck] = (colTotals[ck] || []).concat(rows);
      });
      rowTotals[rk] = aggregateValues(allRowRows, pivotMeasure, pivotAgg);
    });
    // Reduce colTotals row-lists to aggregate values, and compute grand total.
    const colTotalVals = {};
    colKeys.forEach((ck) => { colTotalVals[ck] = aggregateValues(colTotals[ck] || [], pivotMeasure, pivotAgg); });
    grand = aggregateValues(data, pivotMeasure, pivotAgg);
    return { rowKeys, colKeys, cells, rowTotals, colTotals: colTotalVals, grand };
  }, [data, pivotRow, pivotCol, pivotMeasure, pivotAgg]);

  const colors = {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    dark: '#1e293b',
    chartColors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'],
  };

  const formatCurrency = (num) => {
    if (num >= 1000000) return '$' + (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'K';
    return '$' + num.toFixed(2);
  };

  const formatNumber = (num, decimals = 0) => {
    const n = typeof num === 'number' ? num : parseFloat(num);
    if (!Number.isFinite(n)) return '0';
    if (Math.abs(n) >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (Math.abs(n) >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toFixed(decimals);
  };

  const MetricCard = ({ title, value, subtitle, icon, color }) => (
    <div style={{
      backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>{title}</span>
        <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.dark, margin: '0.5rem 0' }}>{value}</div>
      {subtitle && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{subtitle}</span>}
    </div>
  );

  /** Overview card with total + Shop / Field breakdown (ratio from estimate Factors). */
  const ShopFieldCard = ({ title, totalLabel, sf, icon, color, decimals = 0, unit = '' }) => {
    const total = sf?.total || 0;
    const shop = sf?.shop || 0;
    const field = sf?.field || 0;
    const shopPct = Math.round(shopRatio * 1000) / 10;
    const fieldPct = Math.round((100 - shopPct) * 10) / 10;
    const fmt = (v) => `${formatNumber(v, decimals)}${unit ? ` ${unit}` : ''}`;
    return (
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderLeft: `4px solid ${color}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>{title}</span>
          <span style={{ fontSize: '1.5rem' }}>{icon}</span>
        </div>
        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.dark, margin: '0.5rem 0 0.15rem' }}>
          {fmt(total)}
        </div>
        {totalLabel && <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '0.65rem' }}>{totalLabel}</div>}
        <div style={{ height: 6, borderRadius: 4, backgroundColor: '#e2e8f0', overflow: 'hidden', marginBottom: '0.55rem' }}>
          <div style={{ width: `${shopPct}%`, height: '100%', backgroundColor: color, transition: 'width 0.2s' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.78rem' }}>
          <span style={{ color: '#475569' }}>Shop {shopPct}% <strong style={{ color: colors.dark }}>{fmt(shop)}</strong></span>
          <span style={{ color: '#475569' }}>Field {fieldPct}% <strong style={{ color: colors.dark }}>{fmt(field)}</strong></span>
        </div>
      </div>
    );
  };

  // Recharts horizontal bar panel (visual pass, review item 21.iv — replaces
  // the old CSS-div bars). Same props API, so every tab upgrades at once.
  const BarChart = ({ data, valueKey, labelKey, title, maxBars = 10, formatValue }) => {
    const chartData = data.slice(0, maxBars).map((d) => ({ ...d, __v: Number(d[valueKey]) || 0 }));
    const fmt = (v) => (formatValue ? formatValue(v) : formatNumber(v));
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 0.5rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 28 + 40)}>
          <ReBarChart data={chartData} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => fmt(v)} />
            <YAxis type="category" dataKey={labelKey} width={110} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmt(v)} labelStyle={{ fontWeight: 600 }} />
            <Bar dataKey="__v" name={title} radius={[0, 4, 4, 0]}>
              {chartData.map((_, i) => <Cell key={i} fill={colors.chartColors[i % colors.chartColors.length]} />)}
            </Bar>
          </ReBarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Recharts donut panel (visual pass — replaces the old conic-gradient donut).
  const DonutChart = ({ data, valueKey, labelKey, title, formatValue }) => {
    const chartData = data.slice(0, 6).map((d) => ({ name: d[labelKey], value: Number(d[valueKey]) || 0 }));
    const total = chartData.reduce((s, d) => s + d.value, 0);
    const fmt = (v) => (formatValue ? formatValue(v) : formatNumber(v));
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 0.5rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <ResponsiveContainer width="100%" height={230}>
          <PieChart>
            <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%"
              innerRadius={55} outerRadius={85} paddingAngle={2}
              label={(e) => (total > 0 && e.value > 0 ? `${((e.value / total) * 100).toFixed(0)}%` : '')} labelLine={false}>
              {chartData.map((_, i) => <Cell key={i} fill={colors.chartColors[i % colors.chartColors.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => fmt(v)} />
            <Legend wrapperStyle={{ fontSize: '0.72rem' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'materials', label: 'Materials', icon: '🔧' },
    { id: 'breakdown', label: 'Breakdown', icon: '📋' },
    { id: 'summaries', label: 'Summaries', icon: '🧮' },
    { id: 'pivot', label: 'Pivot', icon: '🔀' },
    { id: 'changes', label: 'Change History', icon: '🕘' },
  ];

  const selectCtrlStyle = { padding: '0.4rem 0.6rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem', backgroundColor: 'white', color: colors.dark };
  const labelCtrlStyle = { fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 2, display: 'block' };

  const fmtMeasure = (key, val) => {
    if (val === null || val === undefined) return '—';
    if (MONEY_KEYS.has(key)) return formatCurrency(val);
    return formatNumber(val, Math.abs(val) < 100 && val % 1 !== 0 ? 2 : 0);
  };

  const cellTh = { padding: '0.5rem 0.75rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0', position: 'sticky', top: 0, whiteSpace: 'nowrap' };
  const cellTd = { padding: '0.45rem 0.75rem', fontSize: '0.82rem', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };

  const renderOverview = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard title="Total Items" value={analytics.totalRecords} subtitle={`${analytics.uniqueLines} lines`} icon="📋" color={colors.primary} />
        <MetricCard title="Total Qty" value={formatNumber(analytics.totalQty)} icon="📦" color={colors.info} />
        <MetricCard title="Total CAD" value={formatCurrency(analytics.totalCAD)} subtitle={`mat ${formatCurrency(analytics.totalMaterialCAD)} · lab ${formatCurrency(analytics.totalLabourCAD)}`} icon="💰" color={colors.success} />
        <MetricCard title="Total Hours" value={formatNumber(analytics.totalHR, 1)} icon="⏱️" color={colors.warning} />
        <MetricCard title="Total Joints" value={formatNumber(analytics.totalJoints)} subtitle={`${analytics.byJoinType.length} joint types`} icon="🔗" color={colors.danger} />
      </div>
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem',
        marginBottom: '0.85rem', padding: '0.65rem 0.9rem', background: '#f8fafc',
        borderRadius: 10, border: '1px solid #e2e8f0',
      }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Shop / Field ratio</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#64748b' }}>
          Shop
          <input
            type="number" min="0" max="100" step="0.1"
            value={shopPctInput}
            onChange={(e) => {
              setShopPctInput(e.target.value);
              const pct = parseFloat(e.target.value);
              if (Number.isFinite(pct) && pct >= 0 && pct <= 100) setShopRatio(pct / 100);
            }}
            onBlur={applyShopRatioLocal}
            onKeyDown={(e) => { if (e.key === 'Enter') applyShopRatioLocal(); }}
            style={{ width: 64, padding: '4px 6px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
            title="Percent of work allocated to Shop (rest is Field). Loaded from Factors; Save writes it back."
          />
          %
        </label>
        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
          Field {Math.round((100 - (parseFloat(shopPctInput) || 0)) * 10) / 10}%
        </span>
        {estimateNumber && (
          <button
            type="button"
            onClick={saveShopRatio}
            disabled={shopRatioSaving}
            style={{
              padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5e1',
              background: 'white', color: '#334155', fontSize: '0.72rem', fontWeight: 600,
              cursor: shopRatioSaving ? 'not-allowed' : 'pointer',
            }}
          >
            {shopRatioSaving ? 'Saving…' : 'Save to Factors'}
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <ShopFieldCard
          title="Total Hours — Shop / Field"
          totalLabel="Split by shop / field ratio above"
          sf={analytics.hoursSF}
          icon="⏱️"
          color={colors.warning}
          decimals={1}
        />
        <MetricCard
          title="Total ft"
          value={`${formatNumber(analytics.pipeFt, 1)} ft`}
          subtitle="Qty of Pipe + T-Pipe + V-Pipe"
          icon="📏"
          color={colors.info}
        />
        <ShopFieldCard
          title="Total Dia In — Shop / Field"
          totalLabel="Sum of Total Dia In"
          sf={analytics.diaInSF}
          icon="⌀"
          color={colors.secondary}
          decimals={1}
        />
        <ShopFieldCard
          title="Total Joints — Shop / Field"
          totalLabel="Sum of total joints"
          sf={analytics.jointsSF}
          icon="🔗"
          color={colors.danger}
          decimals={0}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden', maxHeight: '320px', overflowY: 'auto' }}>
          <div style={{ padding: '0.85rem 1rem 0.25rem', fontWeight: 600, color: colors.dark, fontSize: '1rem' }}>
            Pipe by Size
          </div>
          <div style={{ padding: '0 1rem 0.5rem', fontSize: '0.72rem', color: '#94a3b8' }}>
            Type = Pipe / T-Pipe / V-Pipe · Size · Qty (ft)
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={cellTh}>Size</th>
                <th style={{ ...cellTh, textAlign: 'right' }}>Qty (ft)</th>
              </tr>
            </thead>
            <tbody>
              {analytics.pipeBySize.length === 0 ? (
                <tr>
                  <td colSpan={2} style={{ ...cellTd, color: '#94a3b8', textAlign: 'center' }}>No Pipe rows</td>
                </tr>
              ) : analytics.pipeBySize.map((r) => (
                <tr key={r.size}>
                  <td style={{ ...cellTd, fontWeight: 500 }}>{r.size}</td>
                  <td style={{ ...cellTd, textAlign: 'right', fontWeight: 600 }}>{formatNumber(r.qty, 1)}</td>
                </tr>
              ))}
            </tbody>
            {analytics.pipeBySize.length > 0 && (
              <tfoot>
                <tr>
                  <td style={{ ...cellTd, fontWeight: 700, backgroundColor: '#f8fafc' }}>Total</td>
                  <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f8fafc' }}>
                    {formatNumber(analytics.pipeFt, 1)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden', maxHeight: '320px', overflowY: 'auto' }}>
          <div style={{ padding: '0.85rem 1rem 0.25rem', fontWeight: 600, color: colors.dark, fontSize: '1rem' }}>
            Dia In by Joint Type
          </div>
          <div style={{ padding: '0 1rem 0.5rem', fontSize: '0.72rem', color: '#94a3b8' }}>
            Joint Type · Total Dia In
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={cellTh}>Joint Type</th>
                <th style={{ ...cellTh, textAlign: 'right' }}>Dia In</th>
              </tr>
            </thead>
            <tbody>
              {analytics.diaInByJoinType.length === 0 ? (
                <tr>
                  <td colSpan={2} style={{ ...cellTd, color: '#94a3b8', textAlign: 'center' }}>No rows</td>
                </tr>
              ) : analytics.diaInByJoinType.map((r) => (
                <tr key={r.name}>
                  <td style={{ ...cellTd, fontWeight: 500 }}>{r.name}</td>
                  <td style={{ ...cellTd, textAlign: 'right', fontWeight: 600 }}>{formatNumber(r.diaIn, 1)}</td>
                </tr>
              ))}
            </tbody>
            {analytics.diaInByJoinType.length > 0 && (
              <tfoot>
                <tr>
                  <td style={{ ...cellTd, fontWeight: 700, backgroundColor: '#f8fafc' }}>Total</td>
                  <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f8fafc' }}>
                    {formatNumber(analytics.diaInSF.total, 1)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <BarChart
          data={analytics.pipeBySize.map((r) => ({ name: String(r.size), qty: r.qty }))}
          valueKey="qty"
          labelKey="name"
          title="Pipe qty (ft) by Size"
          maxBars={20}
          formatValue={(v) => `${formatNumber(v, 1)} ft`}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
        <DonutChart
          data={[
            { name: 'Material', value: analytics.totalMaterialCAD },
            { name: 'Labour', value: analytics.totalLabourCAD },
            { name: 'Other', value: Math.max(analytics.totalCAD - analytics.totalMaterialCAD - analytics.totalLabourCAD, 0) },
          ].filter((d) => d.value > 0)}
          valueKey="value" labelKey="name" title="Cost split (Material vs Labour)" formatValue={formatCurrency} />
        <DonutChart data={analytics.byType} valueKey="count" labelKey="name" title="Items by Type" />
        <BarChart data={analytics.byType} valueKey="totalCAD" labelKey="name" title="Cost by Type" formatValue={formatCurrency} />
        <BarChart data={analytics.byIso} valueKey="hr" labelKey="name" title="Hours by ISO (top 10)" formatValue={(v) => `${formatNumber(v, 1)} hr`} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(360px, 1fr) minmax(360px, 1fr)', gap: '1rem' }}>
        <BarChart data={analytics.byJoinType} valueKey="joints" labelKey="name" title="Total Joints by Joint Type" />
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden', maxHeight: '360px', overflowY: 'auto' }}>
          <div style={{ padding: '0.85rem 1rem 0.5rem', fontWeight: 600, color: colors.dark, fontSize: '1rem' }}>Joint Type pivot</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={cellTh}>Joint Type</th>
                <th style={{ ...cellTh, textAlign: 'right' }}>Items</th>
                <th style={{ ...cellTh, textAlign: 'right' }}>Qty</th>
                <th style={{ ...cellTh, textAlign: 'right' }}>Total Joints</th>
                <th style={{ ...cellTh, textAlign: 'right' }}>CAD</th>
              </tr>
            </thead>
            <tbody>
              {analytics.byJoinType.map((r) => (
                <tr key={r.name}>
                  <td style={{ ...cellTd, fontWeight: 500 }}>{r.name}</td>
                  <td style={{ ...cellTd, textAlign: 'right' }}>{formatNumber(r.count)}</td>
                  <td style={{ ...cellTd, textAlign: 'right' }}>{formatNumber(r.qty)}</td>
                  <td style={{ ...cellTd, textAlign: 'right', fontWeight: 600 }}>{formatNumber(r.joints)}</td>
                  <td style={{ ...cellTd, textAlign: 'right' }}>{formatCurrency(r.totalCAD)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ ...cellTd, fontWeight: 700, backgroundColor: '#f8fafc' }}>Total</td>
                <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f8fafc' }}>{formatNumber(analytics.totalRecords)}</td>
                <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f8fafc' }}>{formatNumber(analytics.totalQty)}</td>
                <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f8fafc' }}>{formatNumber(analytics.totalJoints)}</td>
                <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f8fafc' }}>{formatCurrency(analytics.totalCAD)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );

  const renderMaterials = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <BarChart data={analytics.byMaterial} valueKey="totalCAD" labelKey="name" title="Cost by Material" formatValue={formatCurrency} />
        <BarChart data={analytics.byMaterial} valueKey="qty" labelKey="name" title="Quantity by Material" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        <BarChart data={analytics.byPipingClass} valueKey="count" labelKey="name" title="Items by Piping Class" />
        <BarChart data={analytics.byPipingClass} valueKey="totalCAD" labelKey="name" title="Cost by Piping Class" formatValue={formatCurrency} />
      </div>
    </>
  );

  const renderBreakdown = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <BarChart data={analytics.byFabShop} valueKey="count" labelKey="name" title="Items by Fabrication Shop" />
        <BarChart data={analytics.byFabShop} valueKey="hr" labelKey="name" title="Hours by Fabrication Shop" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1rem' }}>
        <DonutChart data={analytics.byPriority} valueKey="count" labelKey="name" title="Items by Priority" />
        <BarChart data={analytics.byType} valueKey="hr" labelKey="name" title="Hours by Type" />
      </div>
    </>
  );

  const SUMMARY_MEASURE_OPTS = [
    { key: 'grand_total_cad', label: 'Grand Total CAD' },
    { key: 'total_material_cad', label: 'Material CAD' },
    { key: 'total_labour_cad', label: 'Labour CAD' },
    { key: 'qty', label: 'Quantity' },
    { key: 'total_hours', label: 'Labour Hours' },
    { key: 'joints', label: 'Joints' },
    { key: 'count', label: 'Count' },
  ];

  const renderSummaries = () => {
    const dimLabel = (dimensions.find((d) => d.key === summaryDim) || {}).label || summaryDim;
    const dim2Label = (dimensions.find((d) => d.key === summaryDim2) || {}).label || summaryDim2;
    const has2 = !!summaryDim2 && summaryDim2 !== summaryDim;
    const totals = summaryRows.reduce((acc, r) => {
      acc.count += r.count; acc.qty += r.qty; acc.total_hours += r.total_hours; acc.grand_total_cad += r.grand_total_cad; acc.joints += r.joints; return acc;
    }, { count: 0, qty: 0, total_hours: 0, grand_total_cad: 0, joints: 0 });
    return (
      <>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div>
            <label style={labelCtrlStyle}>Group by</label>
            <select value={summaryDim} onChange={(e) => setSummaryDim(e.target.value)} style={selectCtrlStyle}>
              {dimensions.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelCtrlStyle}>Then by</label>
            <select value={summaryDim2} onChange={(e) => setSummaryDim2(e.target.value)} style={selectCtrlStyle}>
              <option value="">(none)</option>
              {dimensions.filter((d) => d.key !== summaryDim).map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelCtrlStyle}>Chart measure</label>
            <select value={summaryMeasure} onChange={(e) => setSummaryMeasure(e.target.value)} style={selectCtrlStyle}>
              {SUMMARY_MEASURE_OPTS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            {has2 ? `${dimLabel} → ${dim2Label}` : dimLabel} · {summaryRows.length} groups
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(360px, 1fr) minmax(360px, 1.2fr)', gap: '1rem', alignItems: 'start' }}>
          <BarChart
            data={summaryChartRows}
            valueKey={summaryMeasure}
            labelKey="name"
            title={`${(SUMMARY_MEASURE_OPTS.find((m) => m.key === summaryMeasure) || {}).label} by ${dimLabel}${has2 ? ' (layer 1)' : ''}`}
            maxBars={15}
            formatValue={MONEY_KEYS.has(summaryMeasure) ? formatCurrency : undefined}
          />
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden', maxHeight: '60vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={cellTh}>{dimLabel}</th>
                  {has2 && <th style={cellTh}>{dim2Label}</th>}
                  <th style={{ ...cellTh, textAlign: 'right' }}>Count</th>
                  <th style={{ ...cellTh, textAlign: 'right' }}>Qty</th>
                  <th style={{ ...cellTh, textAlign: 'right' }}>Hours</th>
                  <th style={{ ...cellTh, textAlign: 'right' }}>Total CAD</th>
                  <th style={{ ...cellTh, textAlign: 'right' }}>Joints</th>
                </tr>
              </thead>
              <tbody>
                {summaryRows.map((r, idx) => {
                  const showGroup1 = !has2 || idx === 0 || summaryRows[idx - 1].group1 !== r.group1;
                  return (
                    <tr key={r.name}>
                      <td style={{ ...cellTd, fontWeight: showGroup1 ? 600 : 400, color: showGroup1 ? colors.dark : '#94a3b8' }} title={r.group1}>
                        {showGroup1 ? r.group1 : ''}
                      </td>
                      {has2 && <td style={{ ...cellTd, fontWeight: 500 }} title={r.group2}>{r.group2}</td>}
                      <td style={{ ...cellTd, textAlign: 'right' }}>{formatNumber(r.count)}</td>
                      <td style={{ ...cellTd, textAlign: 'right' }}>{formatNumber(r.qty)}</td>
                      <td style={{ ...cellTd, textAlign: 'right' }}>{formatNumber(r.total_hours, 1)}</td>
                      <td style={{ ...cellTd, textAlign: 'right' }}>{formatCurrency(r.grand_total_cad)}</td>
                      <td style={{ ...cellTd, textAlign: 'right' }}>{formatNumber(r.joints)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ position: 'sticky', bottom: 0 }}>
                  <td style={{ ...cellTd, fontWeight: 700, backgroundColor: '#f8fafc' }} colSpan={has2 ? 2 : 1}>Total</td>
                  <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f8fafc' }}>{formatNumber(totals.count)}</td>
                  <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f8fafc' }}>{formatNumber(totals.qty)}</td>
                  <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f8fafc' }}>{formatNumber(totals.total_hours, 1)}</td>
                  <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f8fafc' }}>{formatCurrency(totals.grand_total_cad)}</td>
                  <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f8fafc' }}>{formatNumber(totals.joints)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderPivot = () => {
    const fmtCell = (v) => (pivotAgg === 'count' ? formatNumber(v, 0) : fmtMeasure(pivotMeasure, v));
    const rowLabel = (dimensions.find((d) => d.key === pivotRow) || {}).label || pivotRow;
    const measureLabel = (measures.find((m) => m.key === pivotMeasure) || {}).label || pivotMeasure;
    const hasCols = !!pivotCol;
    return (
      <>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div>
            <label style={labelCtrlStyle}>Rows</label>
            <select value={pivotRow} onChange={(e) => setPivotRow(e.target.value)} style={selectCtrlStyle}>
              {dimensions.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelCtrlStyle}>Columns</label>
            <select value={pivotCol} onChange={(e) => setPivotCol(e.target.value)} style={selectCtrlStyle}>
              <option value="">(none)</option>
              {dimensions.filter((d) => d.key !== pivotRow).map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelCtrlStyle}>Aggregation</label>
            <select value={pivotAgg} onChange={(e) => setPivotAgg(e.target.value)} style={selectCtrlStyle}>
              {AGGREGATIONS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelCtrlStyle}>Value</label>
            <select value={pivotMeasure} onChange={(e) => setPivotMeasure(e.target.value)} style={selectCtrlStyle} disabled={pivotAgg === 'count'}>
              {measures.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            {pivotAgg === 'count' ? 'Count of rows' : `${(AGGREGATIONS.find((a) => a.key === pivotAgg) || {}).label} of ${measureLabel}`}
            {' · '}{pivot.rowKeys.length} rows{hasCols ? ` × ${pivot.colKeys.length} cols` : ''}
          </span>
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'auto', maxHeight: '62vh' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...cellTh, left: 0, position: 'sticky', zIndex: 2 }}>{rowLabel}</th>
                {hasCols
                  ? pivot.colKeys.map((ck) => <th key={ck} style={{ ...cellTh, textAlign: 'right' }} title={ck}>{ck}</th>)
                  : <th style={{ ...cellTh, textAlign: 'right' }}>{pivotAgg === 'count' ? 'Count' : measureLabel}</th>}
                {hasCols && <th style={{ ...cellTh, textAlign: 'right' }}>Total</th>}
              </tr>
            </thead>
            <tbody>
              {pivot.rowKeys.map((rk) => (
                <tr key={rk}>
                  <td style={{ ...cellTd, fontWeight: 500, position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }} title={rk}>{rk}</td>
                  {hasCols
                    ? pivot.colKeys.map((ck) => <td key={ck} style={{ ...cellTd, textAlign: 'right' }}>{fmtCell(pivot.cells[rk][ck])}</td>)
                    : <td style={{ ...cellTd, textAlign: 'right' }}>{fmtCell(pivot.rowTotals[rk])}</td>}
                  {hasCols && <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f8fafc' }}>{fmtCell(pivot.rowTotals[rk])}</td>}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ ...cellTd, fontWeight: 700, backgroundColor: '#f1f5f9', position: 'sticky', left: 0 }}>Total</td>
                {hasCols
                  ? pivot.colKeys.map((ck) => <td key={ck} style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f1f5f9' }}>{fmtCell(pivot.colTotals[ck])}</td>)
                  : <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#f1f5f9' }}>{fmtCell(pivot.grand)}</td>}
                {hasCols && <td style={{ ...cellTd, textAlign: 'right', fontWeight: 700, backgroundColor: '#e2e8f0' }}>{fmtCell(pivot.grand)}</td>}
              </tr>
            </tfoot>
          </table>
        </div>
      </>
    );
  };

  const CHANGE_TYPE_LABELS = {
    new_iso_version: { label: 'New ISO version', color: colors.warning },
    qty_increase: { label: 'Qty increase', color: colors.info },
    new_iso: { label: 'New ISO', color: colors.success },
  };

  const renderChanges = () => {
    if (changeLoading) {
      return <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading change history…</div>;
    }
    if (changeError) {
      return <div style={{ padding: '2rem', textAlign: 'center', color: colors.danger }}>{changeError}</div>;
    }
    const events = changeEvents || [];
    if (events.length === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem', color: '#64748b' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>🕘</div>
          <h3 style={{ margin: 0, color: colors.dark }}>No change events yet</h3>
          <p style={{ margin: '0.5rem 0 0', textAlign: 'center', maxWidth: 460 }}>
            Change events are logged automatically when a new MTO revision introduces a re-issued ISO
            version or a quantity increase on an awarded job.
          </p>
        </div>
      );
    }
    const fmtNum = (v) => (v === null || v === undefined || v === '' ? '—' : formatNumber(parseFloat(v), Math.abs(parseFloat(v)) < 100 && parseFloat(v) % 1 !== 0 ? 1 : 0));
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'auto', maxHeight: '70vh' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={cellTh}>When</th>
              <th style={cellTh}>Rev</th>
              <th style={cellTh}>ISO Sheet</th>
              <th style={cellTh}>Change</th>
              <th style={{ ...cellTh, textAlign: 'right' }}>Version</th>
              <th style={{ ...cellTh, textAlign: 'right' }}>Qty (old → new)</th>
              <th style={{ ...cellTh, textAlign: 'right' }}>Hours (old → new)</th>
              <th style={cellTh}>Hold</th>
              <th style={cellTh}>CCR</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => {
              const ct = CHANGE_TYPE_LABELS[e.change_type] || { label: e.change_type, color: '#64748b' };
              return (
                <tr key={e.id}>
                  <td style={cellTd}>{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</td>
                  <td style={cellTd}>{e.prev_rev_no !== null && e.prev_rev_no !== undefined ? `${e.prev_rev_no} → ${e.rev_no}` : e.rev_no}</td>
                  <td style={{ ...cellTd, fontWeight: 500 }} title={e.iso_sheet_number}>{e.iso_sheet_number || '—'}</td>
                  <td style={cellTd}>
                    <span style={{ display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: 999, fontSize: '0.72rem', fontWeight: 600, color: 'white', backgroundColor: ct.color }}>{ct.label}</span>
                  </td>
                  <td style={{ ...cellTd, textAlign: 'right' }}>{(e.old_version || e.new_version) ? `${e.old_version ?? '—'} → ${e.new_version ?? '—'}` : '—'}</td>
                  <td style={{ ...cellTd, textAlign: 'right' }}>{fmtNum(e.old_qty)} → {fmtNum(e.new_qty)}</td>
                  <td style={{ ...cellTd, textAlign: 'right' }}>{fmtNum(e.old_hours)} → {fmtNum(e.new_hours)}</td>
                  <td style={cellTd}>{e.on_hold ? <span style={{ color: colors.danger, fontWeight: 600 }}>On hold</span> : '—'}</td>
                  <td style={cellTd}>{e.ccr_id ? `#${e.ccr_id}` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
      zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem',
    }}>
      <div style={{
        backgroundColor: '#f1f5f9', borderRadius: '16px', width: '100%', maxWidth: '1400px', maxHeight: '95vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      }}>
        <div style={{
          padding: '1.25rem 1.5rem', background: `linear-gradient(135deg, ${colors.info} 0%, ${colors.primary} 100%)`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>📐 MTO Analytics</h2>
            <p style={{ margin: '0.25rem 0 0', opacity: 0.9, fontSize: '0.85rem' }}>Material take-off quantities, costs, and estimation analysis</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
            <button onClick={onClose} style={{
              padding: '0.5rem 1rem', backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white',
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem',
            }}>✕ Close</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setSelectedView(tab.id)} style={{
              padding: '0.75rem 1.25rem', backgroundColor: selectedView === tab.id ? colors.info : 'transparent',
              color: selectedView === tab.id ? 'white' : '#64748b', border: 'none', borderRadius: '8px 8px 0 0',
              cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {analytics.totalRecords === 0 && selectedView !== 'changes' ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
              <h3 style={{ margin: 0, color: colors.dark }}>No Data Available</h3>
              <p style={{ margin: '0.5rem 0 0' }}>Add MTO data to see analytics</p>
            </div>
          ) : (
            <>
              {selectedView === 'overview' && renderOverview()}
              {selectedView === 'materials' && renderMaterials()}
              {selectedView === 'breakdown' && renderBreakdown()}
              {selectedView === 'summaries' && renderSummaries()}
              {selectedView === 'pivot' && renderPivot()}
              {selectedView === 'changes' && renderChanges()}
            </>
          )}
        </div>

        <div style={{
          padding: '0.75rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#94a3b8',
        }}>
          <span>{analytics.totalRecords} items | {analytics.uniqueTypes} types | {analytics.uniqueMaterials} materials</span>
          <span>Total: {formatCurrency(analytics.totalCAD)} | {formatNumber(analytics.totalHR, 1)} hrs | {formatNumber(analytics.totalJoints)} joints</span>
        </div>
      </div>
    </div>
  );
};

export default MtoAnalyticsDashboard;

